import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogTrigger,
  IconButton,
  SkeletonSpinnerCentered,
  SvgIcon,
  Text,
} from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { updateFile as apiUpdateFile, getStoredFileDownloadUrl } from '~/api/projects2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { getReasonForError } from '~/common/getReasonForError';
import { notifyDownloadError } from '~/common/notifyDownloadError';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { useClickAwayListener } from '~/hooks/useClickAwayListener';
import { usePendingDownload } from '~/hooks/usePendingDownload';
import type { ProjectFile } from './common';
import DeleteFile from './DeleteFile';
import MediaViewer from './MediaViewer';
import PDFViewer from './PDFViewer';

interface Props {
  file: ProjectFile;
  deleteFile: (file: ProjectFile, type: 'files' | 'media') => Promise<void>;
  fileType: 'files' | 'media';
  loadProject: () => Promise<void>;
}

/** Hands a project file to the browser's download manager through a signed attachment URL. */
export const streamProjectFile = async (file: ProjectFile, type: 'files' | 'media', projectId: string) => {
  let download: Awaited<ReturnType<typeof getStoredFileDownloadUrl>>;
  try {
    download = await getStoredFileDownloadUrl(projectId, 'project-file', file.id);
  } catch (err) {
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectFileDownloadFailed, {
      project_id: projectId,
      file_id: file.id,
      file_name: file.name,
      file_type: file.type,
      delivery: 'browser',
      source: type,
      reason: getReasonForError(err),
    });
    notifyDownloadError();
    return;
  }

  // The URL is signed as an attachment, so navigating to it saves the file and leaves the page in place.
  window.location.href = download.url;

  captureTypedPosthogEvent(POSTHOG_EVENTS.projectFileDownloaded, {
    project_id: projectId,
    file_id: file.id,
    file_name: file.name,
    file_type: file.type,
    file_size: download.filesize,
    delivery: 'browser',
    source: type,
  });
};

export default function FileItem({ file, deleteFile, fileType, loadProject }: Props) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const { hasClickedAway } = useClickAwayListener();
  const inputRef = useRef<HTMLInputElement>(null);
  const { pendingId, track } = usePendingDownload();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    setIsEditing(false);
    setFileName(removeExtension(file.name));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (hasClickedAway) {
      setFileName(removeExtension(file.name));
    }
  }, [hasClickedAway]);

  const removeExtension = (str: string): string => {
    const splitStringArr = str.split('.');

    // remove last element i.e file extension
    splitStringArr.pop();

    return splitStringArr.join('.');
  };

  const getExtension = (str: string): string => {
    const ext = str.split('.').pop();
    if (ext) return ext;
    else throw new Error('Filename missing extension');
  };

  const submitEdit = async (_e: any) => {
    setIsEditing(false);
    setLoading(true);

    try {
      const nameWithExt = `${fileName}.${getExtension(file.name)}`;
      await apiUpdateFile(projectId, file.id, {
        file_name: nameWithExt,
      });
      await loadProject();
    } catch (err: any) {
      console.error('Error while updating file: ', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    file: ProjectFile,
    type: 'files' | 'media',
  ) => {
    e.stopPropagation();
    track(file.id, () => streamProjectFile(file, type, projectId));
  };

  const getFileType = (t: 'files' | 'media'): string => {
    if (t === 'media') {
      return file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : '';
    } else if (t === 'files') {
      if (file.type === 'application/pdf') {
        return 'pdf';
      }
      return '';
    }

    return '';
  };

  // after isEditing state updates, if editing is on, focus the input.
  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const fittedName = (): string => {
    const name = file.name || '';
    if (name.length > 45) return `${name.slice(0, 20)}...${name.slice(-20)}`;
    else return name;
  };

  return (
    <div className="flex w-full items-center gap-3 overflow-hidden rounded-[8px] border border-grey-30 h-12 px-3">
      {/* File icon */}
      <div className="flex h-8 shrink-0 items-center justify-center text-primary">
        <SvgIcon icon="file" size={24} />
      </div>

      {/* File info */}
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        {loading ? (
          <SkeletonSpinnerCentered />
        ) : isEditing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitEdit(e);
            }}
            className="min-w-0 flex-1"
          >
            <input
              ref={inputRef}
              onBlur={submitEdit}
              className="h-full w-full min-w-0 rounded border border-grey-30 bg-white px-2 py-0.5 text-sm text-grey-80 outline-none focus:border-focus"
              value={fileName || ''}
              onChange={(e) => setFileName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitEdit(e);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsEditing(false);
                  setFileName(removeExtension(file.name));
                }
              }}
            />
          </form>
        ) : (
          <button
            type="button"
            className="cursor-pointer min-w-0 text-left bg-transparent border-none p-0 w-full"
            onClick={() => {
              setFileName(removeExtension(file.name));
              setIsEditing(true);
            }}
          >
            <Text className="text-sm font-medium text-primary block" title={file.name}>
              {fittedName()}
            </Text>
          </button>
        )}
      </div>

      {/* Action buttons */}
      {!loading && (
        <div className="flex shrink-0 items-center gap-2">
          <IconButton
            icon="download"
            variant="secondary"
            size="icon"
            aria-label={`Download ${file.name}`}
            data-testid={`project-file-download-${file.id}`}
            loading={pendingId === file.id}
            onClick={(e) => downloadFile(e, file, fileType)}
          />

          {/* NOTE: Previews only for PDFs, images, and videos. */}
          {getFileType(fileType) === 'pdf' && (
            <Dialog>
              <DialogTrigger asChild>
                <IconButton
                  icon="eyeOpen"
                  variant="secondary"
                  size="icon"
                  aria-label={`Preview ${file.name}`}
                  data-testid={`project-file-preview-${file.id}`}
                />
              </DialogTrigger>
              <DialogContent
                title={file.name || 'File preview'}
                className="z-[110] w-auto min-w-min max-w-[75vw]"
                overlayClassName="z-[100]"
                data-testid={`project-file-preview-${file.id}-dialog`}
              >
                <DialogBody>
                  <PDFViewer file={file} />
                </DialogBody>
              </DialogContent>
            </Dialog>
          )}

          {(getFileType(fileType) === 'image' || getFileType(fileType) === 'video') && (
            <Dialog>
              <DialogTrigger asChild>
                <IconButton
                  icon="eyeOpen"
                  variant="secondary"
                  size="icon"
                  aria-label={`Preview ${file.name}`}
                  data-testid={`project-file-preview-${file.id}`}
                />
              </DialogTrigger>
              <DialogContent
                title={file.name || 'File preview'}
                className="z-[110] w-auto min-w-min max-w-[75vw]"
                overlayClassName="z-[100]"
                data-testid={`project-file-preview-${file.id}-dialog`}
              >
                <DialogBody>
                  <MediaViewer file={file} type={getFileType(fileType) as 'image' | 'video'} />
                </DialogBody>
              </DialogContent>
            </Dialog>
          )}
          <Can action={Action.DeleteProjectFile}>
            <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
              <DialogTrigger asChild>
                <IconButton
                  variant="danger"
                  icon="trash"
                  size="icon"
                  aria-label={`Delete ${file.name}`}
                  data-testid={`project-file-delete-trigger-${file.id}`}
                />
              </DialogTrigger>
              <DialogContent
                title="Delete files?"
                className="z-[110] w-auto min-w-min max-w-[75vw]"
                overlayClassName="z-[100]"
                data-testid={`project-file-delete-${file.id}-dialog`}
              >
                <DialogBody>
                  <DeleteFile
                    file={file}
                    deleteFile={deleteFile}
                    close={() => setIsDeleteOpen(false)}
                    type={fileType}
                  />
                </DialogBody>
              </DialogContent>
            </Dialog>
          </Can>
        </div>
      )}
    </div>
  );
}
